<script setup>
import {computed, ref, watch} from 'vue'
import SiteTreeContentAreas from './SiteTreeContentAreas.vue'
import Button from '@/common/Button.vue'
import ItemStatusIcons from "@/common/ItemStatusIcons.vue"
import SiteTreePageItemSelectMenu from "./SiteTreePageItemSelectMenu.vue"
import {usePageTemplatesContentAreasStore} from '@/stores/pageTemplatesContentAreas'
import {usePageSectionsStore} from '@/stores/pageSections'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useItemDataStore} from '@/stores/itemData'
import {useSiteTreeStore} from '@/stores/siteTree'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    siteTreeId: String,
    pageId: Number
})

const appStore = useAppStore()
const pageTemplatesContentAreasStore = usePageTemplatesContentAreasStore()
const pageSectionsStore = usePageSectionsStore()
const pageContentDataStore = usePageContentDataStore()
const itemDataStore = useItemDataStore()
const siteTreeStore = useSiteTreeStore(props.siteTreeId)

// Initialize page sections
pageSectionsStore.init()

// Reactive ref for asynchronous page data
const page = ref(null)

// Watch props.pageId and fetch data natively
watch(() => props.pageId, async (newPageId) => {
    if (newPageId) {
        page.value = await itemDataStore.get({
            tableName: 'pages',
            id: newPageId
        })
    } else {
        page.value = null
    }
}, {immediate: true})

const parentChildIds = computed(() => {
    return siteTreeStore.parentChildIds || {}
})

const expandedPageIds = computed(() => {
    return siteTreeStore.expandedPageIds || []
})

const selectedPageId = computed(() => {
    return siteTreeStore.selectedPageId
})

const selectedContentId = computed(() => {
    return siteTreeStore.selectedContentId
})

const selectedContentsPageId = computed(() => {
    if (selectedContentId.value) {
        return pageContentDataStore.items?.find((obj) => obj.id === selectedContentId.value)?.pageId
    }
    return undefined
})

const lastSelectedPageId = computed(() => {
    return siteTreeStore.lastSelectedPageId
})

const hasChildPages = computed(() => {
    return !!parentChildIds.value[props.pageId]?.length
})

const isChecked = computed(() => {
    if (propagatedContentId.value) {
        return !!siteTreeStore.propagationData?.find(o => o.pageId === props.pageId)
    }
    return false
})

const isActive = computed(() => {
    return selectedPageId.value === props.pageId
})

const isSelectedContentsPageId = computed(() => {
    return selectedContentsPageId.value === props.pageId
})

const isOpen = computed(() => {
    return expandedPageIds.value.indexOf(props.pageId) > -1
})

const showChildPages = computed(() => {
    return !!parentChildIds.value[props.pageId]?.length && isOpen.value
})

const isHomePage = computed(() => {
    return !!(appStore.settings?.homePage && appStore.settings.homePage === props.pageId)
})

const isLastSelected = computed(() => {
    return (
        selectedPageId.value === 0 &&
        lastSelectedPageId.value === props.pageId
    )
})

const showContent = computed(() => {
    return siteTreeStore.showContent
})

const hasContent = computed(() => {
    if (showContent.value) {
        return !!pageContentDataStore.items?.filter(o => o.pageId === props.pageId).length
    }
    return false
})

const selectContentMode = computed(() => {
    return siteTreeStore.selectContentMode
})

const propagatedContentId = computed(() => {
    return siteTreeStore.propagatedContentId
})

const selectedContentArea = computed(() => {
    return pageContentDataStore.items?.find(
        o => o.pageId === props.pageId && o.contentId === propagatedContentId.value
    )?.contentArea
})

const sections = computed(() => {
    return (pageSectionsStore.items || []).filter(o => o.pageId === props.pageId)
})

const togglePage = () => {
    expandedPageIds.value.indexOf(props.pageId) === -1
        ? siteTreeStore.addExpandedPageId(props.pageId)
        : siteTreeStore.removeExpandedPageId(props.pageId)
}

const onPageClick = async () => {
    if (selectContentMode.value) {
        togglePage()
        return
    }

    if (propagatedContentId.value) {
        if (!!siteTreeStore.propagationData?.find(o => o.pageId === props.pageId)) {
            siteTreeStore.unsetPropagationData(props.pageId)
            return
        }

        // Selecting the first option by default removes the need to validate the menus
        const contentArea = sections.value[0]?.areas[0]?.value
            || pageTemplatesContentAreasStore.pageTemplatesContentAreas?.[page.value?.templateId]?.[0]

        siteTreeStore.setPropagationData({pageId: props.pageId, contentArea})
    } else if (siteTreeStore.onPageSelect) {
        siteTreeStore.onPageSelect(props.pageId)
    }
}

const onIconClick = () => {
    togglePage()
}
</script>

<template>
    <div v-if="page">
        <a href="#" @click.prevent="onPageClick"
           class="list-group-item list-group-item-action d-flex align-items-center gap-2 border-0 small"
           :class="{'p-1 pe-2': !propagatedContentId, 'p-0 px-2': propagatedContentId, 'active': isLastSelected || isSelectedContentsPageId || isActive}">

            <!-- Toggle open/close page content -->
            <Button @click.prevent.stop="onIconClick" :disabled="!hasChildPages && !hasContent"
                    class="btn-sm p-0"
                    :default-class="'btn-transparent'">
                <i :class="{ 'bi-chevron-down': isOpen, 'bi-chevron-right': !isOpen, 'invisible': !hasChildPages && !hasContent }"/>
            </Button>

            <div class="flex-fill d-inline-flex align-items-center gap-2">
                <template v-if="propagatedContentId">
                    <span :class="{'bi-square': !isChecked, 'bi-check-square': isChecked}"/>

                    <span class="flex-fill d-inline-block py-2">{{ page.title }}</span>

                    <SiteTreePageItemSelectMenu v-if="isChecked && !selectedContentArea" :site-tree-id="siteTreeId"
                                                :page-id="pageId"/>
                </template>

                <span v-else>{{ page.title }}</span>

                <small class="text-body-secondary">{{ page.pageLabel }}</small>
            </div>

            <i v-if="isHomePage" class="bi-house" title="Home page"/>

            <i v-if="page.hidden" class="bi-eye-slash-fill"/>
            <ItemStatusIcons :table-name="'pages'" :id="pageId" :is-active="isActive"/>
        </a>

        <SiteTreeContentAreas v-if="hasContent && isOpen" :page-id="pageId" :site-tree-id="siteTreeId"/>

        <div v-if="showChildPages" class="ps-3">
            <SiteTreePageItem v-for="childPageId in parentChildIds[pageId]" :key="childPageId" :page-id="childPageId"
                              :site-tree-id="siteTreeId"/>
        </div>
    </div>
</template>